import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Spinner } from '@/components/atoms/Spinner';
import { formatTimeSpent } from '@/utils/formatTime';
import type { StudentObjectives } from '@/services/api/users.api';
import { ROUTES } from '@/constants/routes';
import { cn } from '@/utils/cn';
import styles from './PerformanceSection.module.scss';

export interface PerformanceSectionProps {
  objectives: StudentObjectives | null;
  isLoading: boolean;
  /** Suma de `timeSpent` del historial (mismo mes), solo si la API no envía `totalStudyTimeSecondsThisMonth`. */
  fallbackStudySecondsFromHistory?: number;
}

export function PerformanceSection({
  objectives,
  isLoading,
  fallbackStudySecondsFromHistory = 0,
}: PerformanceSectionProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  if (!isLoading && !objectives) {
    return null;
  }

  const studySeconds =
    objectives != null && typeof objectives.totalStudyTimeSecondsThisMonth === 'number'
      ? objectives.totalStudyTimeSecondsThisMonth
      : fallbackStudySecondsFromHistory;
  const challengesDone = objectives?.storiesCompletedThisMonth ?? 0;
  const accuracyMonth =
    typeof objectives?.averageAccuracyThisMonth === 'number'
      ? objectives.averageAccuracyThisMonth
      : 0;
  const avgMinutesPerChallengeMonth =
    typeof objectives?.averageTimePerStoryThisMonth === 'number'
      ? objectives.averageTimePerStoryThisMonth
      : 0;

  return (
    <section className={styles.performance} aria-labelledby="progress-performance-heading">
      <div className={styles.performance__message}>
        <h2 id="progress-performance-heading" className={styles.performance__title}>
          {t('progressPage.performanceTitle')}
        </h2>
      </div>
      {isLoading ? (
        <div className={styles.performance__loading}>
          <Spinner size="md" aria-label={t('common.loading')} />
        </div>
      ) : (
        <div className={styles.performance__stats}>
          <button
            type="button"
            className={cn(styles.performance__statCard, styles.performance__statCard_action)}
            data-no-press-scale=""
            onClick={() => navigate(ROUTES.STUDENT.PROGRESS_STORIES_MONTH)}
            aria-label={t('progressPage.performanceStoriesCardAria')}
          >
            <span className={styles.performance__statValue}>{challengesDone}</span>
            <span className={styles.performance__statLabel}>
              {t('progressPage.performanceStoriesCard')}
            </span>
          </button>
          <button
            type="button"
            className={cn(styles.performance__statCard, styles.performance__statCard_action)}
            data-no-press-scale=""
            onClick={() => navigate(ROUTES.STUDENT.PROGRESS_STUDY_TIME)}
            aria-label={t('progressPage.performanceStudyTimeCardAria')}
          >
            <span className={styles.performance__statValue}>{formatTimeSpent(studySeconds)}</span>
            <span className={styles.performance__statLabel}>
              {t('progressPage.performanceStudyTimeCard')}
            </span>
          </button>
          <div className={styles.performance__statCard}>
            <span className={styles.performance__statValue}>
              {t('progressPage.minutesShort', { count: avgMinutesPerChallengeMonth })}
            </span>
            <span className={styles.performance__statLabel}>
              {t('progressPage.performanceAvgTimeCard')}
            </span>
          </div>
          <div className={styles.performance__statCard}>
            <span className={styles.performance__statValue}>{accuracyMonth}%</span>
            <span className={styles.performance__statLabel}>
              {t('progressPage.performanceAccuracyCard')}
            </span>
          </div>
        </div>
      )}
    </section>
  );
}

PerformanceSection.displayName = 'PerformanceSection';
